import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Library, Mail, Lock } from 'lucide-react';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { signIn, signOut, user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const isSigningIn = useRef(false);

  // Handle redirect to admin when the user is verified as admin
  useEffect(() => {
    if (!isSigningIn.current && user && isAdmin) {
      navigate('/admin');
    }
  }, [user, isAdmin, navigate]);

  // If logged in but not admin, sign out (but not during an active sign-in attempt)
  useEffect(() => {
    if (!isSigningIn.current && user && !isAdmin) {
      signOut();
    }
  }, [user, isAdmin, signOut]);

  // Show loading while signing out non-admin user
  if (!isSigningIn.current && user && !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Signing out...</p>
        </div>
      </div>
    );
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    try {
      setIsLoading(true);
      isSigningIn.current = true;
      await signIn(email, password);
      navigate('/admin');
    } catch (error) {
      // Error is already handled in the auth context
      console.error(error);
    } finally {
      setIsLoading(false);
      isSigningIn.current = false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 bg-gradient-to-br from-background via-background to-secondary/20">
      <div className="w-full max-w-md mx-auto space-y-6">
        <div className="text-center flex flex-col items-center">
          <div className="bg-primary rounded-xl p-3 mb-4 text-primary-foreground">
            <Library size={28} />
          </div>
          <h1 className="text-3xl font-bold tracking-tighter mb-1">Repsheets Panel</h1>
          <p className="text-sm text-muted-foreground max-w-xs mx-auto">
            Admin access only. Enter your administrator credentials.
          </p>
        </div>

        <div className="bg-card p-6 rounded-xl shadow-subtle border border-border/40 overflow-hidden backdrop-blur-sm">
          <form onSubmit={handleSignIn} className="space-y-5 pt-2">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Mail size={14} className="text-muted-foreground" />
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="bg-background/70"
                required
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Lock size={14} className="text-muted-foreground" />
                  Password
                </label>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="bg-background/70"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full transition-all mt-4"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></span>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </div>
        
        <div className="text-center text-xs text-muted-foreground mt-8">
          <p>&copy; {new Date().getFullYear()} Repsheets. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
